import { useMutation } from '@tanstack/react-query';
import { LabTest } from '../schemas/shared/lab';
import { RandoxHomeTestKitParams, RandoxInClinicParams, InuviOrderParams } from '../api/api.randox';
import useApi from './useApi';

export const CreateRandoxOrderTypes = {
  RANDOX_IN_CLINIC: 'RANDOX_IN_CLINIC',
  HOME_TEST_KIT: 'HOME_TEST_KIT',
  INUVI_IN_PERSON: 'INUVI_IN_PERSON'
} as const;

export const combineOrderAddressParts = (address: LabTest['patient_address']) => {
  return [
    address?.addline1,
    address?.addline2,
    address?.addline3,
    address?.addline4,
    address?.region,
    address?.country,
    address?.postcode
  ]
    .filter(Boolean)
    .join(', ');
};

export const combineOrderLabTests = (tests: LabTest['lab_tests']) => {
  return tests
    .map(
      ({ randox_code, service_type_code, service_type_desc }) =>
        `${service_type_desc}(${randox_code || service_type_code})`
    )
    .join(', ');
};

export const hasOrderUrineTest = (tests: LabTest['lab_tests']) => {
  return tests.some((test) => test.urine_test) || false;
};

const useCreateRandoxOrderMutation = () => {
  const { Api } = useApi();

  const { mutateAsync: createRandoxInClinicOrder } = useMutation({
    mutationKey: ['createRandoxInClinicOrder'],
    mutationFn: Api.randox.createRandoxInClinicOrder
  });

  const { mutateAsync: createPatientHomeTestKitOrder } = useMutation({
    mutationKey: ['createPatientHomeTestKitOrder'],
    mutationFn: Api.randox.createPatientHomeTestKitOrder
  });

  const { mutateAsync: createInuviOrder } = useMutation({
    mutationKey: ['createPatientInuviOrder'],
    mutationFn: Api.randox.createInuviOrder
  });

  return (type: keyof typeof CreateRandoxOrderTypes, data: RandoxHomeTestKitParams | RandoxInClinicParams) => {
    switch (type) {
      case CreateRandoxOrderTypes.HOME_TEST_KIT: {
        return createPatientHomeTestKitOrder(data as RandoxHomeTestKitParams);
      }
      case CreateRandoxOrderTypes.RANDOX_IN_CLINIC: {
        return createRandoxInClinicOrder(data as RandoxInClinicParams);
      }
      case CreateRandoxOrderTypes.INUVI_IN_PERSON: {
        return createInuviOrder(data as InuviOrderParams);
      }
      default: {
        break;
      }
    }
  };
};

export default useCreateRandoxOrderMutation;
